import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Upload, FileText, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAlert } from '../../context/AlertContext';
import { formatRoleName } from '../../utils/roleUtils';

// Show "Name (Human Readable Role)" — never the technical role id.
const formatReaderLabel = (u) => {
  const role = formatRoleName(u.role);
  return role ? `${u.name} (${role})` : u.name;
};

const DocumentEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useAlert();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [document, setDocument] = useState(null);
  const [hasFile, setHasFile] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    document_type: 'SOP',
    category: 'Safety',
    department_id: '',
    location: '',
    applicable_roles: [],
    keywords: '',
    mandatory_reading: false,
    target_readers: [],
    content: ''
  });
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [titleError, setTitleError] = useState('');

  useEffect(() => {
    fetchDocument();
    fetchUsers();
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/departments`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setDepartments(res.data.departments || []))
      .catch(() => setDepartments([]));
  }, [id]);

  const fetchDocument = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const doc = response.data;
      setDocument(doc);

      // Check if document is in Draft status
      if (doc.current_status !== 'Draft') {
        showError('Only draft documents can be edited');
        navigate(`/documents/${id}`);
        return;
      }

      // Get latest version content
      const versions = doc.versions || [];
      const latestVersion = versions.length > 0 ? versions[versions.length - 1] : {};
      const hasFileContent = latestVersion.file_path ? true : false;
      setHasFile(hasFileContent);

      // Populate form data
      const keywords = doc.keywords || [];
      setFormData({
        title: doc.title || '',
        description: doc.description || '',
        document_type: doc.document_type || 'SOP',
        category: doc.category || 'Safety',
        department_id: doc.department_id || '',
        location: doc.location || '',
        applicable_roles: doc.applicable_roles || [],
        keywords: Array.isArray(keywords) ? keywords.join(', ') : keywords,
        mandatory_reading: doc.mandatory_reading || false,
        target_readers: doc.target_readers || [],
        content: latestVersion.content || ''
      });
    } catch (error) {
      console.error('Error fetching document:', error);
      showError('Document not found');
      navigate('/documents');
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Same minimal directory endpoint the create form uses — works for
      // editors without the users:read permission (QA #36).
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/directory`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const checkTitleExists = async (title) => {
    // Same reason as on the create form: `required` accepts a field of spaces,
    // so renaming a document to whitespace was possible.
    if (!title.trim()) {
      setTitleError(title.length ? 'Title cannot be only spaces' : '');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/check-title`,
        {
          params: { title: title.trim(), exclude_id: id },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.exists) {
        setTitleError('A document with this title already exists');
      } else {
        setTitleError('');
      }
    } catch (error) {
      // Silently fail - backend will still validate on submit
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'title' && titleError) {
      setTitleError('');
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 50 * 1024 * 1024) {
        showError('File size exceeds 50MB limit');
        return;
      }
      const allowedTypes = ['.pdf', '.docx', '.xlsx', '.pptx', '.jpg', '.jpeg', '.png'];
      const fileExt = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      if (!allowedTypes.includes(fileExt)) {
        showError(`File type ${fileExt} not allowed. Allowed types: ${allowedTypes.join(', ')}`);
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleMultiSelect = (e, field) => {
    const options = Array.from(e.target.selectedOptions, option => option.value);
    setFormData({ ...formData, [field]: options });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanTitle = formData.title.trim();
    if (!cleanTitle) {
      setTitleError('Title is required and cannot be only spaces');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      // Step 1: Update document metadata
      const keywordsArray = formData.keywords.split(',').map(k => k.trim()).filter(k => k);
      const updatePayload = {
        title: cleanTitle,
        description: formData.description,
        document_type: formData.document_type,
        category: formData.category,
        department_id: formData.department_id || null,
        location: formData.location,
        applicable_roles: formData.applicable_roles,
        keywords: keywordsArray,
        mandatory_reading: formData.mandatory_reading,
        target_readers: formData.target_readers
      };

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}`,
        updatePayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Step 2: Upload new file or update content if changed
      if (file) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('version_type', 'Minor');
        formDataUpload.append('change_description', 'Updated after review');

        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/upload`,
          formDataUpload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percentCompleted);
            }
          }
        );
      } else if (!hasFile && formData.content) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/content`,
          {
            content: formData.content,
            version_type: 'Minor',
            change_description: 'Updated after review'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      showSuccess('Document updated successfully!');
      navigate(`/documents/${id}`);
    } catch (error) {
      console.error('Error updating document:', error);
      showError(error.response?.data?.detail || 'Failed to update document');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!document) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate(`/documents/${id}`)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Document
          </Button>

          <h1 className="text-3xl font-bold text-gray-900">Edit Document</h1>
          <p className="text-gray-600 mt-1">
            {document.document_number} - Update document content and metadata
          </p>
        </div>

        {/* Document Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">Document Information</h2>

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                onBlur={(e) => checkTitleExists(e.target.value)}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  titleError ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Lockout/Tagout Procedure"
              />
              {titleError && (
                <p className="mt-1 text-sm text-red-600">{titleError}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type <span className="text-red-500">*</span>
              </label>
              <select
                name="document_type"
                value={formData.document_type}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="SOP">SOP</option>
                <option value="Work_Instruction">Work Instruction</option>
                <option value="Policy">Policy</option>
                <option value="Form">Form</option>
                <option value="Checklist">Checklist</option>
                <option value="Manual">Manual</option>
                <option value="SDS">SDS (Safety Data Sheet)</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of the document"
            />
          </div>

          {/* Classification */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="Safety">Safety</option>
                <option value="Operations">Operations</option>
                <option value="Quality">Quality</option>
                <option value="Environmental">Environmental</option>
                <option value="HR">HR</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department
              </label>
              <select
                name="department_id"
                value={formData.department_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Select department —</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Plant A, Building 2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keywords (comma-separated)
            </label>
            <input
              type="text"
              name="keywords"
              value={formData.keywords}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., lockout, tagout, energy isolation"
            />
          </div>

          {/* Applicable Roles */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Applicable Roles (Hold Ctrl/Cmd to select multiple)
            </label>
            <select
              multiple
              value={formData.applicable_roles}
              onChange={(e) => handleMultiSelect(e, 'applicable_roles')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-24"
            >
              <option value="Safety Manager">Safety Manager</option>
              <option value="Safety Officer">Safety Officer</option>
              <option value="Supervisor">Supervisor</option>
              <option value="Operator">Operator</option>
              <option value="Technician">Technician</option>
              <option value="Engineer">Engineer</option>
              <option value="Contractor">Contractor</option>
            </select>
            {formData.applicable_roles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.applicable_roles.map(role => (
                  <span key={role} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                    {role}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Mandatory Reading */}
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="mandatory_reading"
                checked={formData.mandatory_reading}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Mandatory Reading (requires acknowledgment)
              </span>
            </label>
          </div>

          {/* Target Readers */}
          {formData.mandatory_reading && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Readers (Hold Ctrl/Cmd to select multiple) <span className="text-red-500">*</span>
              </label>
              <select
                multiple
                value={formData.target_readers}
                onChange={(e) => handleMultiSelect(e, 'target_readers')}
                required={formData.mandatory_reading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 h-32"
              >
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {formatReaderLabel(user)}
                  </option>
                ))}
              </select>
              {formData.target_readers.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">{formData.target_readers.length} user(s) selected</p>
                </div>
              )}
            </div>
          )}

          {/* Content / File Upload */}
          {hasFile ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Replace Document File (optional)
              </label>
              {(() => {
                const versions = document.versions || [];
                const latest = versions[versions.length - 1] || {};
                if (!latest.file_path) return null;
                const fmtSize = (bytes) => {
                  if (bytes == null) return '';
                  if (bytes < 1024) return `${bytes} B`;
                  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
                };
                return (
                  <div className="mb-3 flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-600 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 break-all">
                        {latest.file_name || 'Uploaded file'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {latest.file_size != null ? `${fmtSize(latest.file_size)} • ` : ''}
                        Version {latest.version_number}
                      </p>
                    </div>
                  </div>
                );
              })()}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.docx,.xlsx,.pptx,.jpg,.jpeg,.png"
                />
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center justify-center cursor-pointer"
                >
                  <Upload className="w-12 h-12 text-gray-400 mb-2" />
                  <p className="text-gray-600 text-sm mb-1">
                    Click to upload a new version
                  </p>
                  <p className="text-gray-500 text-xs">
                    PDF, DOCX, XLSX, PPTX, JPG, PNG (max 50MB)
                  </p>
                </label>
                {file && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-700 font-medium">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        setUploadProgress(0);
                        const fileInput = window.document.getElementById('file-upload');
                        if (fileInput) fileInput.value = '';
                      }}
                      className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                    >
                      Remove file
                    </button>
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}
                {!file && (
                  <p className="mt-2 text-sm text-gray-500 text-center">
                    Leave empty to keep the current file
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Content
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                rows={15}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="Write your document content here..."
              />
              <p className="text-sm text-gray-500 mt-1">
                Tip: You can paste content from Word, Google Docs, or other sources.
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/documents/${id}`)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !!titleError || !formData.title.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentEdit;
